import { randomIntInclusive, type SeededRandom } from '../seeded-random';

import {
  countTilesWithValue,
  fillRectWithValue,
  rectFitsWithGap,
  setTileValue,
  tileIndexAt,
  tileValueAt
} from './tile-grid';
import type { MapGenTileCoord } from './tile-layout-types';
import { bfsWalkableVisitedFrom } from './tile-reachability';

/**
 * Places straight-line and single-bend L stamps, then short scatter strips,
 * until the tile budget is reached or placement attempts are exhausted.
 * Returns how many tiles came from line stamps vs scatter (before reachability carving).
 */
export function placeLineAndScatterTiles(args: {
  grid: Uint8Array;
  width: number;
  height: number;
  budget: number;
  gapTiles: number;
  minPlacementSizeTiles: number;
  maxPlacementSizeTiles: number;
  fillValue: number;
  rand: SeededRandom;
}): { lineTileCount: number; scatterTileCount: number } {
  const { grid, width, height, budget, gapTiles, minPlacementSizeTiles, maxPlacementSizeTiles, fillValue, rand } = args;
  const before = countTilesWithValue(grid, fillValue);
  const placement: PlacementRules = {
    gapTiles,
    minSizeTiles: minPlacementSizeTiles,
    maxSizeTiles: maxPlacementSizeTiles,
    fillValue,
    rand
  };

  const lineTarget = Math.floor(budget * 0.7);
  placeStraightAndLStamps(grid, width, height, lineTarget, placement);
  const afterLines = countTilesWithValue(grid, fillValue);
  const lineTileCount = afterLines - before;

  topUpFillWithScatterStrips(grid, width, height, budget, placement);
  const afterScatter = countTilesWithValue(grid, fillValue);
  const scatterTileCount = afterScatter - afterLines;

  return { lineTileCount, scatterTileCount };
}

/**
 * Fills remaining budget with short gapped strips.
 * Shared by line-scatter and other fill layouts after their primary wall pass.
 */
export function topUpFillWithScatterStrips(
  grid: Uint8Array,
  width: number,
  height: number,
  budget: number,
  placement: {
    gapTiles: number;
    minSizeTiles: number;
    maxSizeTiles: number;
    fillValue: number;
    rand: SeededRandom;
  }
): number {
  const before = countTilesWithValue(grid, placement.fillValue);
  scatterTileStrips(grid, width, height, budget, {
    gapTiles: placement.gapTiles,
    minSizeTiles: placement.minSizeTiles,
    maxSizeTiles: placement.maxSizeTiles,
    fillValue: placement.fillValue,
    rand: placement.rand
  });
  return countTilesWithValue(grid, placement.fillValue) - before;
}

interface PlacementRules {
  gapTiles: number;
  minSizeTiles: number;
  maxSizeTiles: number;
  fillValue: number;
  rand: SeededRandom;
}

/**
 * Spends most of the line budget on straight and L stamps with open-gap spacing.
 * Prefers L shapes so the map gets bent walls rather than only corridors.
 */
function placeStraightAndLStamps(
  grid: Uint8Array,
  width: number,
  height: number,
  lineTarget: number,
  placement: PlacementRules
): void {
  const firstLPlaced = tryPlaceL(grid, width, height, lineTarget, placement);
  const maxAttempts = Math.max(80, lineTarget * (8 + (placement.gapTiles * 2)));
  let attempts = 0;

  while (countTilesWithValue(grid, placement.fillValue) < lineTarget && attempts < maxAttempts) {
    attempts++;
    const preferL = !firstLPlaced || attempts % 3 !== 0;
    const placed = preferL
      ? tryPlaceL(grid, width, height, lineTarget, placement)
      : tryPlaceStraight(grid, width, height, lineTarget, placement);

    if (!placed && preferL && attempts % 4 === 0) {
      tryPlaceStraight(grid, width, height, lineTarget, placement);
    }
    else if (!placed && !preferL) {
      tryPlaceL(grid, width, height, lineTarget, placement);
    }
  }
}

function tryPlaceStraight(
  grid: Uint8Array,
  width: number,
  height: number,
  budget: number,
  placement: PlacementRules
): boolean {
  const remaining = remainingTileBudget(grid, budget, placement.fillValue);
  const maxSize = Math.min(placement.maxSizeTiles, remaining, Math.max(width, height));
  if (maxSize < placement.minSizeTiles) { return false; }

  for (let length = maxSize; length >= placement.minSizeTiles; length--) {
    const horizontal = placement.rand() < 0.5;
    const wTiles = horizontal ? length : 1;
    const hTiles = horizontal ? 1 : length;
    const colMax = Math.max(0, width - wTiles);
    const rowMax = Math.max(0, height - hTiles);
    const col = randomIntInclusive(placement.rand, 0, colMax);
    const row = randomIntInclusive(placement.rand, 0, rowMax);

    const rect = { x: col, y: row, width: wTiles, height: hTiles };
    if (!rectFitsWithGap(grid, width, height, rect, placement.gapTiles, placement.fillValue)) {
      continue;
    }
    fillRectWithValue(grid, width, height, rect, budget, placement.fillValue);
    return true;
  }

  return false;
}

/** Single-bend L: horizontal arm and vertical arm share one corner tile. */
function tryPlaceL(
  grid: Uint8Array,
  width: number,
  height: number,
  budget: number,
  placement: PlacementRules
): boolean {
  const remaining = remainingTileBudget(grid, budget, placement.fillValue);
  const maxSize = Math.min(placement.maxSizeTiles, remaining, width + height - 2);
  if (maxSize < placement.minSizeTiles || maxSize < 3) { return false; }

  for (let totalSize = maxSize; totalSize >= placement.minSizeTiles; totalSize--) {
    if (totalSize < 3) { break; }

    for (let cornerTry = 0; cornerTry < 6; cornerTry++) {
      const cells = buildRandomLCells(width, height, totalSize, placement.rand);
      if (!cellsFitWithGap(grid, width, height, cells, placement.gapTiles, placement.fillValue)) {
        continue;
      }

      for (const cell of cells) {
        setTileValue(grid, width, height, cell.col, cell.row, placement.fillValue);
      }
      return true;
    }
  }

  return false;
}

function buildRandomLCells(width: number, height: number, totalSize: number, rand: SeededRandom): MapGenTileCoord[] {
  const armH = randomIntInclusive(rand, 2, totalSize - 1);
  const armV = totalSize - armH + 1;
  const hSign = rand() < 0.5 ? 1 : -1;
  const vSign = rand() < 0.5 ? 1 : -1;
  const cornerCol = randomIntInclusive(rand, 0, Math.max(0, width - 1));
  const cornerRow = randomIntInclusive(rand, 0, Math.max(0, height - 1));
  const cells: MapGenTileCoord[] = [{ col: cornerCol, row: cornerRow }];

  for (let i = 1; i < armH; i++) {
    cells.push({ col: cornerCol + (hSign * i), row: cornerRow });
  }
  for (let i = 1; i < armV; i++) {
    cells.push({ col: cornerCol, row: cornerRow + (vSign * i) });
  }

  return cells;
}

function cellsFitWithGap(
  grid: Uint8Array,
  width: number,
  height: number,
  cells: readonly MapGenTileCoord[],
  gapTiles: number,
  fillValue: number
): boolean {
  for (const cell of cells) {
    if (cell.col < 0 || cell.row < 0 || cell.col >= width || cell.row >= height) {
      return false;
    }

    if (tileValueAt(grid, width, height, cell.col, cell.row) !== 0) {
      return false;
    }
  }

  if (gapTiles <= 0) {
    return true;
  }

  const cellKeys = new Set(cells.map((c) => `${c.col},${c.row}`));
  for (const cell of cells) {
    for (let r = cell.row - gapTiles; r <= cell.row + gapTiles; r++) {
      for (let c = cell.col - gapTiles; c <= cell.col + gapTiles; c++) {
        if (cellKeys.has(`${c},${r}`)) {
          continue;
        }

        if (tileValueAt(grid, width, height, c, r) === fillValue) {
          return false;
        }
      }
    }
  }

  return true;
}

function scatterTileStrips(
  grid: Uint8Array,
  width: number,
  height: number,
  budget: number,
  placement: PlacementRules
): void {
  const maxAttempts = width * height * (4 + placement.gapTiles);
  let attempts = 0;

  while (remainingTileBudget(grid, budget, placement.fillValue) >= placement.minSizeTiles && attempts < maxAttempts) {
    attempts++;
    const remaining = remainingTileBudget(grid, budget, placement.fillValue);
    const maxSize = Math.min(placement.maxSizeTiles, remaining, Math.max(width, height));

    for (let length = maxSize; length >= placement.minSizeTiles; length--) {
      const horizontal = placement.rand() < 0.5;
      const wTiles = horizontal ? length : 1;
      const hTiles = horizontal ? 1 : length;
      const colMax = Math.max(0, width - wTiles);
      const rowMax = Math.max(0, height - hTiles);
      const col = randomIntInclusive(placement.rand, 0, colMax);
      const row = randomIntInclusive(placement.rand, 0, rowMax);

      const rect = { x: col, y: row, width: wTiles, height: hTiles };
      if (!rectFitsWithGap(grid, width, height, rect, placement.gapTiles, placement.fillValue)) {
        continue;
      }
      fillRectWithValue(grid, width, height, rect, budget, placement.fillValue);
      break;
    }
  }
}

function remainingTileBudget(grid: Uint8Array, budget: number, fillValue: number): number {
  return budget - countTilesWithValue(grid, fillValue);
}

/**
 * Ensures every 0-valued tile is reachable from the first 0-valued tile via 4-neighbors.
 * Carves the shortest Manhattan connector when islands exist. Returns tiles cleared.
 */
export function ensureBaseTilesReachable(grid: Uint8Array, width: number, height: number): number {
  let carved = 0;
  let guard = 0;
  const maxPasses = width * height;

  while (guard < maxPasses) {
    guard++;
    const reach = getBaseReachability(grid, width, height);
    if (reach.unreachable.length === 0) {
      break;
    }

    const target = reach.unreachable[0];
    const bridge = findNearestReachable(reach.visited, width, height, target);
    if (!bridge) {
      break;
    }
    carved += carveConnector(grid, width, height, bridge, target);
  }

  return carved;
}

interface BaseReachability {
  visited: Uint8Array;
  unreachable: MapGenTileCoord[];
}

function getBaseReachability(grid: Uint8Array, width: number, height: number): BaseReachability {
  const start = findFirstBaseTile(grid, width, height);
  const unreachable: MapGenTileCoord[] = [];

  if (!start) {
    return { visited: new Uint8Array(grid.length), unreachable };
  }

  const startIndex = tileIndexAt(start.col, start.row, width);
  const isBaseTile = (index: number) => grid[index] === 0;
  const visited = bfsWalkableVisitedFrom(width, height, startIndex, isBaseTile);

  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      const index = tileIndexAt(col, row, width);
      if (grid[index] === 0 && visited[index] === 0) {
        unreachable.push({ col, row });
      }
    }
  }

  return { visited, unreachable };
}

function findFirstBaseTile(grid: Uint8Array, width: number, height: number): MapGenTileCoord | null {
  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      if (grid[tileIndexAt(col, row, width)] === 0) {
        return { col, row };
      }
    }
  }
  return null;
}

function findNearestReachable(
  visited: Uint8Array,
  width: number,
  height: number,
  target: MapGenTileCoord
): MapGenTileCoord | null {
  let best: MapGenTileCoord | null = null;
  let bestDist = Infinity;

  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      const index = tileIndexAt(col, row, width);
      if (visited[index] !== 1) {
        continue;
      }
      const dist = Math.abs(col - target.col) + Math.abs(row - target.row);
      if (dist < bestDist) {
        bestDist = dist;
        best = { col, row };
      }
    }
  }

  return best;
}

function carveConnector(
  grid: Uint8Array,
  width: number,
  height: number,
  from: MapGenTileCoord,
  to: MapGenTileCoord
): number {
  let carved = 0;
  let col = from.col;
  let row = from.row;

  while (col !== to.col) {
    col += col < to.col ? 1 : -1;
    if (tileValueAt(grid, width, height, col, row) !== 0) {
      setTileValue(grid, width, height, col, row, 0);
      carved++;
    }
  }
  while (row !== to.row) {
    row += row < to.row ? 1 : -1;
    if (tileValueAt(grid, width, height, col, row) !== 0) {
      setTileValue(grid, width, height, col, row, 0);
      carved++;
    }
  }

  return carved;
}

/** True when every 0-valued tile is in one 4-connected component. */
export function areBaseTilesFullyReachable(grid: Uint8Array, width: number, height: number): boolean {
  return getBaseReachability(grid, width, height).unreachable.length === 0;
}
